import * as React from 'react';
import Modal from 'antd/lib/modal';
import message from 'antd/lib/message';
import { StatusTransitionAction, KycRejectionReasonOptions, KycRejectionReasonKeys, BlacklistedMessage } from './Masters.Helper';
import { useGenericState } from 'src/Library/UseGenericState';
import { KycData } from './Types.masters';
import { fetchKycData, changeConnectionStatus } from 'src/api/masters';
import { ConfirmConnectionState } from './Masters.interface';
import Form, { FormComponentProps } from 'antd/lib/form';
import withStyles from 'react-jss';
import { commonStyles } from 'src/Library/CommonStyles';
import { StylesProps } from 'src/Library/Types';
import Col from 'antd/lib/col';
import Button from 'antd/lib/button';
import Radio from 'antd/lib/radio';
import Input from 'antd/lib/input';
import Loader from 'src/components/common/Loader';
import ViewKYC from './ViewKYC';
import { getFormattedDateTime } from 'src/Library/Helper';

interface IProps extends ConfirmConnectionState, StylesProps<ReturnType<typeof confirmConnectionRequestStyles>>,
  FormComponentProps {
    visible: boolean;
    onClose: (success?: boolean) => void;
  }

interface StateProps {
  kycData: KycData[];
  isLoading: boolean;
  disabled: boolean;
}

const confirmConnectionRequestStyles = (theme) => ({
  ...commonStyles(theme),
  organisationText: {
    marginTop: '20px',
    marginLeft: '20px',
    width: '80px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  rejectionOptions: {
    '& .ant-radio-wrapper': {
      margin: '15px 5px',
    },
  },
  lastStatusUpdatedAt: {
    fontSize: '10px',
    fontStyle: 'italic',
    color: '#666666',
    textAlign: 'right',
  },
  blackListedText: {
    fontSize: '12px',
    fontStyle: 'italic',
    color: '#767676',
    wordBreak: 'break-word',
    lineHeight: '15px',
    marginLeft: '29px',
    marginTop: '-10px',
  },
});

const ConfirmConnectionRequestModal = (props: IProps) => {
  const {
    onboardingId,
    statusTransition,
    organisationLogoUrl,
    organisationName,
    visible,
    classes,
    lastStatusUpdatedAt,
    form,
  } = props;

  const {
    getFieldDecorator,
    getFieldValue,
  } = form;

  const [state, setState] = useGenericState<StateProps>({
      kycData: [],
      isLoading: false,
      disabled: false,
  });

  const {
    kycData,
    isLoading,
    disabled,
  } = state;

  const loadKycData = async () => {
    setState({
      isLoading: true
    });
    const response = await fetchKycData({
      onboardingId,
    });
    if (!response.isSuccess) {
      message.error(response.errorMessage)
    }
    setState({
      isLoading: false,
      kycData: response.isSuccess ? response.data.data : [],
    });
  };

  React.useEffect(() => {
    loadKycData();
  }, []);

  const handleClose = (success = false) => {
    props.onClose(success);
  }

  const handleSubmit = async () => {
    setState({
      disabled: true,
    })
    form.validateFields(async (err, val) => {
      if (err) {
        setState({
          disabled: false,
        })
        return;
      }
      const action = getFieldValue('rejectionOption') || statusTransition;
      const rejectionRemark = getFieldValue('remarks')?.trim();
      const response = await changeConnectionStatus({
        onboardingId,
        action,
        ...(rejectionRemark && {
          rejectionRemark,
        }),
      });
      setState({
        disabled: false,
      })
      if (response.isSuccess) {
        handleClose(true);
      } else {
        message.error(response.errorMessage);
      }
    });
  };

  const renderOrganisationInfo = () => {
    return <div
      className={classes.flexRowDiv}
      style={{
        backgroundColor: '#FFFFFF',
        height: '67px',
        margin: '30px'
      }}>{organisationLogoUrl && <div>
        <img src={organisationLogoUrl}
        style={{
          maxWidth: '150px',
          width: 'auto',
          maxHeight: '67px',
        }}/></div>}
      <div className={classes.organisationText}>{organisationName}</div>
      <div style={{
        position: 'absolute',
        right: '40px',
        top: '40px',
      }}>
        <div style={{ fontWeight: 'bold' }}>Request Received</div>
        <div className={classes.lastStatusUpdatedAt}>
          {lastStatusUpdatedAt
            ? getFormattedDateTime('DD MMM YYYY', lastStatusUpdatedAt)
            : getFormattedDateTime('DD MMM YYYY')
          }
        </div>
      </div>
    </div>;
  }

  const renderApproveContent = () => {
    return <React.Fragment>
      <div style={{
        color: '#333333',
        fontSize: '16px',
      }}>Are you sure you want to approve the linking request?
        Make sure you have seen and verified all the KYC details.
      </div>
    </React.Fragment>
  }

  const renderRejectionContent = () => {
    const radioOptionKeys = Object.keys(KycRejectionReasonOptions);
    return <React.Fragment>
      <div>Please select the reason of rejection</div>
      <div>
        <Form.Item style={{ marginTop: '15px' }}>
          {getFieldDecorator('rejectionOption', {
            initialValue: KycRejectionReasonKeys.REJECT,
          })(<Radio.Group className={classes.rejectionOptions}>
          {radioOptionKeys.map((key) => {
            return (
              <Radio value={key} style={{ 
                display: 'block'
              }}>
                <span>{KycRejectionReasonOptions[key]}</span>
              </Radio>
            )})}
          </Radio.Group>)}
          <div className={classes.blackListedText}>{BlacklistedMessage}</div>
        </Form.Item>
        <div>Add Remark*</div>
        <Form.Item style={{ marginTop: '15px' }}>
          {getFieldDecorator('remarks', {
            rules: [{ required: true, message: 'Remark is required' }],
          })(
            <Input.TextArea rows={3} style={{ width: '100%' }}
              placeholder="Remark"
            />,
          )}
        </Form.Item>
      </div>
    </React.Fragment>
  }

  const renderClickActions = (): React.ReactElement => {
    return <div style={{
      marginTop: '30px',
      textAlign: 'right'
    }}>
      <Button style={{
          border: '0'
        }}
        onClick={() => handleClose()}
        disabled={disabled}
      >Not Now!</Button>
      <Button type="primary"
        style={{
          backgroundColor: '#006EC3',
        }}
        onClick={handleSubmit}
        loading={disabled}
      >Yes</Button>
    </div>;
  }

  const renderModalBody = () => {
    return <React.Fragment>
      <Col span={10}>
        {renderOrganisationInfo()}
        <div className={classes.flexColumn} style={{
          margin: '30px',
          background: '#FFFFFF',
          padding: '20px'
        }}>
          {statusTransition === StatusTransitionAction.APPROVE && renderApproveContent()}
          {statusTransition === StatusTransitionAction.REJECT && renderRejectionContent()}
          {renderClickActions()}
        </div>
      </Col>
      <Col span={14}><ViewKYC kycData={kycData}/></Col>
    </React.Fragment>
  }

  return <Modal
    visible={visible}
    closable={true}
    footer={false}
    width={'100%'}
    title={'Confirm Connection Request'}
    className={classes.modal}
    onCancel={() => handleClose()}
    style={{ top: 0 }}
  >
    <div
      className={classes.flexRowDiv}
      style={{ backgroundColor: '#F5F4F2', minHeight: '740px'}}
    >
      {isLoading ? <Loader zIndex={1000} /> : renderModalBody()}
    </div>
  </Modal>;
};

export default withStyles(confirmConnectionRequestStyles)(Form.create()(ConfirmConnectionRequestModal));
